import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { TypedMemo } from '../../../common/types';
import { getWidgetIconByWidgetType } from '../../utils/getWidgetIconByWidgetType';
import { useEditorContext } from '../SMLEditorContext';
import { AddBlockRequest, useAddBlock } from '../picker/useAddBlock';
import { blockMessages as m } from './blockMessages';
import { blockTypeLabel } from './describeBlock';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLBlockList.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface Starter {
  request: AddBlockRequest;
  line: string;
}

interface StarterRowProps {
  starter: Starter;
  name: string;
  disabled: boolean;
  onAdd: (starter: Starter) => void;
}

const StarterRowComponent: React.FC<StarterRowProps> = ({ starter, name, disabled, onAdd }) => {
  const intl = useIntl();
  const add = useCallback(() => onAdd(starter), [onAdd, starter]);
  return (
    <div className={styles.starter}>
      <span className={styles.starterIcon}>
        <FontAwesomeIcon icon={getWidgetIconByWidgetType(starter.request.type)} />
      </span>
      <span className={styles.starterText}>
        {name}
        <small>{starter.line}</small>
      </span>
      <button type="button" className={styles.starterAdd} onClick={add} disabled={disabled}>
        <FontAwesomeIcon icon={faPlus} />
        {intl.formatMessage(m.startAdd)}
      </button>
    </div>
  );
};

const StarterRow = TypedMemo(StarterRowComponent);

/**
 * A page with no block (design a3): three starters by page kind, "Add all three" and "See all
 * blocks". A song page: Catalog links, Player, Social links. The Bio Link: Profile, Social links and
 * Catalog links of the newest song. No page header (owner decision 4).
 */
const SMLEmptyStartersComponent: React.FC = () => {
  const intl = useIntl();
  const { isBioLink, url } = useEditorContext();
  const { add, songs } = useAddBlock();
  const [isBusy, setBusy] = useState(false);
  const song = isBioLink ? songs.newestSong : (songs.pageSong ?? songs.newestSong);

  const starters = useMemo<Starter[]>(() => {
    const catalog: Starter = {
      request: { type: SmartLinkWidgetType.presave, afterUUID: null, catalogItemUuid: song?.uuid },
      line: isBioLink
        ? intl.formatMessage(m.startCatalogLinksBio)
        : song === null
          ? intl.formatMessage(m.startCatalogLinksNoSong)
          : intl.formatMessage(m.startCatalogLinks, { title: song.title }),
    };
    const social: Starter = {
      request: { type: SmartLinkWidgetType.socialLinks, afterUUID: null },
      line: intl.formatMessage(m.startSocial),
    };
    if (isBioLink) {
      return [
        { request: { type: SmartLinkWidgetType.profile, afterUUID: null }, line: intl.formatMessage(m.startProfile) },
        social,
        catalog,
      ];
    }
    return [
      catalog,
      { request: { type: SmartLinkWidgetType.player, afterUUID: null }, line: intl.formatMessage(m.startPlayer) },
      social,
    ];
  }, [intl, isBioLink, song]);

  const onAdd = useCallback(
    (starter: Starter) => {
      setBusy(true);
      add(starter.request)
        .catch(console.error)
        .finally(() => setBusy(false));
    },
    [add]
  );
  const addAll = useCallback(() => {
    const run = async (): Promise<void> => {
      setBusy(true);
      const added: string[] = [];
      // In order, one after the other: each block goes at the end of the page.
      for (const starter of starters) {
        // eslint-disable-next-line no-await-in-loop
        const uuid = await add(starter.request, false);
        if (uuid !== null) {
          added.push(uuid);
        }
      }
      if (added[0] !== undefined) {
        url.openBlock(added[0]);
      }
    };
    run()
      .catch(console.error)
      .finally(() => setBusy(false));
  }, [add, starters, url]);
  const seeAll = useCallback(() => url.openPicker(null), [url]);

  return (
    <div className={styles.starters} data-testid="editor-starters">
      <h3 className={styles.title}>{intl.formatMessage(m.startTitle)}</h3>
      <p className={styles.startersSub}>{intl.formatMessage(isBioLink ? m.startSubBioLink : m.startSub)}</p>
      {starters.map((starter) => (
        <StarterRow
          key={starter.request.type}
          starter={starter}
          name={blockTypeLabel(intl, starter.request.type)}
          disabled={isBusy}
          onAdd={onAdd}
        />
      ))}
      <div className={styles.startersActions}>
        <button type="button" className={styles.headAdd} onClick={addAll} disabled={isBusy}>
          <FontAwesomeIcon icon={faPlus} />
          {intl.formatMessage(m.startAddAll)}
        </button>
        <button type="button" className={styles.addBlock} onClick={seeAll}>
          {intl.formatMessage(m.startSeeAll)}
        </button>
      </div>
    </div>
  );
};

export const SMLEmptyStarters = TypedMemo(SMLEmptyStartersComponent);
